// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import FancyScroll, { FancyScrollRefProps } from "@eisinc/App/Components/Common/FancyScroll";
import ManagedAccordionSection from "@eisinc/MobiusOverrides/AccordionSection/ManagedAccordionSection";
import CheckboxGroup, { CheckboxGroupProps } from "@eisinc/MobiusOverrides/CheckboxGroup/CheckboxGroup";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import Checkbox, {
    CheckboxPresentationProps,
    StyleProps as CheckboxStyleProps,
} from "@insite/mobius/Checkbox/Checkbox";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface ProductListFilterAccordionSectionStyles {
    accordionSection?: AccordionSectionPresentationProps;
    checkBoxGroup?: CheckboxGroupProps;
    checkBox?: CheckboxPresentationProps;
    checkBoxSelected?: CheckboxPresentationProps;
    seeAllLink?: LinkPresentationProps;
    seeLessLink?: LinkPresentationProps;
    // XNG-Changes
    facetCount?: TypographyPresentationProps;
    scrollableContainer?: InjectableCss;
    facetName?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    section?: InjectableCss;
}

export const productListFilterAccordionSectionStyles: ProductListFilterAccordionSectionStyles = {
    accordionSection: {
        panelProps: {
            css: css`
                overflow-y: auto;
                overflow-x: hidden;
                max-height: 350px;
            `,
        },
    },
    checkBoxGroup: {
        css: css`
            width: 100%;
            &:not(:last-child) {
                padding-bottom: 5px;
            }
        `,
    },
    checkBox: {
        css: css<CheckboxStyleProps>`
            align-items: flex-start;
            span[role="checkbox"] {
                ${({ _sizeVariant }) =>
                    _sizeVariant === "small"
                        ? css`
                              margin-top: 5px;
                          `
                        : css`
                              margin-top: 4px;
                          `}
            }
        `,
        typographyProps: {
            css: css`
                word-break: break-all;
            `,
        },
    },
    checkBoxSelected: {
        css: css<CheckboxStyleProps>`
            align-items: flex-start;
            span[role="checkbox"] {
                ${({ _sizeVariant }) =>
                    _sizeVariant === "small"
                        ? css`
                              margin-top: 5px;
                          `
                        : css`
                              margin-top: 4px;
                          `}
            }
        `,
        typographyProps: {
            css: css`
                word-break: break-all;
            `,
            weight: "bold",
        },
    },
    seeAllLink: {
        css: css`
            padding-top: 5px;
        `,
    },
    seeLessLink: {
        css: css`
            padding-top: 5px;
        `,
    },
};

export interface Props {
    title: string;
    facets: FacetModel[];
    onChangeFacet: (facet: FacetModel) => void;
    onChangeFacetKnowledge: (facet: FacetModel, title: string, facets: any) => void;
    showMoreLimit: number;
    extendedStyles?: ProductListFilterAccordionSectionStyles;
    fromKnowledgeCenter?: boolean;
    expandByDefault: boolean;
    // XNG-Change
    dontDisplayInAccordion?: boolean;
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        customSettings: getSettingsCollection(state).customSettings,
    };
};

type OwnProps = Props & ReturnType<typeof mapStateToProps>;

const ProductListFiltersAccordionSection: FC<OwnProps> = ({
    title,
    facets,
    fromKnowledgeCenter,
    onChangeFacet,
    onChangeFacetKnowledge,
    showMoreLimit,
    expandByDefault,
    extendedStyles,
    // XNG-Change
    customSettings,
    dontDisplayInAccordion,
}) => {
    const [styles] = React.useState(() => mergeToNew(productListFilterAccordionSectionStyles, extendedStyles));
    const [expanded, setExpanded] = useState(false);

    // <XNG-Changes>
    const [toggled, setToggled] = useState(false);
    const parentBoxRef = React.createRef<HTMLDivElement>();
    const fancyScrollRef = React.createRef<FancyScrollRefProps>();

    showMoreLimit = (customSettings as any).maximumAttributeValues || 5;

    let limitedFacets = expanded ? [...facets] : [...facets]?.sort((a, b) => b.count - a.count).slice(0, showMoreLimit);

    // <XNG-Changes>
    React.useEffect(() => {
        fancyScrollRef.current?.updateScrollable();
    }, [expanded, toggled]);
    // </XNG-Changes

    const anySelected = facets.find(f => f.selected) !== undefined;

    const isScrollable = limitedFacets.length > showMoreLimit;

    const orderItemsByCount = () => {
        const tempFacets = [...facets];
        tempFacets.sort((a, b) => b.count - a.count);
        limitedFacets = tempFacets?.slice(0, showMoreLimit);
    };

    const orderItemsAlphabetical = () => {
        const tempFacets = [...facets];
        limitedFacets = tempFacets;
    };

    if (dontDisplayInAccordion) {
        return (
            <StyledWrapper {...styles.section}>
                <Typography {...styles.title}>{title}</Typography>
                {limitedFacets?.map(facet => (
                    <CheckboxGroup key={facet.id} {...styles.checkBoxGroup}>
                        <Checkbox
                            {...styles.checkBox}
                            checked={facet.selected}
                            onChange={() => {
                                onChangeFacet(facet);
                            }}
                            data-test-selector={`facetFilter${facet.id}`}
                        >
                            <Typography {...styles.facetName}>{facet.name}</Typography>
                        </Checkbox>
                    </CheckboxGroup>
                ))}
            </StyledWrapper>
        );
    }

    return (
        <ManagedAccordionSection
            title={title}
            {...styles.accordionSection}
            initialExpanded={expandByDefault || anySelected}
            onTogglePanel={open => setToggled(open)}
        >
            {/* xng-change - use fieldset to enhance the accessibility */}
            <FancyScroll showArrow={false} refElement={parentBoxRef} ref={fancyScrollRef}>
                <StyledWrapper
                    className="scrollable-container"
                    ref={parentBoxRef}
                    {...{
                        scrollable: isScrollable,
                        limitNumber: 9 + +isScrollable,
                    }}
                    {...styles.scrollableContainer}
                >
                    {limitedFacets?.map(facet => (
                        <CheckboxGroup key={facet.id} {...styles.checkBoxGroup}>
                            <Checkbox
                                {...styles.checkBox}
                                checked={facet.selected}
                                onChange={() => {
                                    if (fromKnowledgeCenter) {
                                        onChangeFacetKnowledge(facet, title, facets);
                                    } else {
                                        onChangeFacet(facet);
                                    }
                                }}
                                data-test-selector={`facetFilter${facet.id}`}
                            >
                                <Typography {...styles.facetName}>{facet.name}</Typography>
                                {facet?.count && facet?.count !== -1 && (
                                    <Typography {...styles.facetCount}>{`(${facet.count})`}</Typography>
                                )}
                                {(facet as any)?.docCount && (facet as any)?.docCount !== -1 && (
                                    <Typography {...styles.facetCount}>{`(${(facet as any)?.docCount})`}</Typography>
                                )}
                            </Checkbox>
                        </CheckboxGroup>
                    ))}
                    {facets &&
                        facets.length > showMoreLimit &&
                        (expanded ? (
                            <Link
                                {...styles.seeLessLink}
                                onClick={() => {
                                    orderItemsByCount();
                                    setExpanded(false);
                                }}
                            >
                                {translate("Show Less")}
                            </Link>
                        ) : (
                            <Link
                                {...styles.seeAllLink}
                                onClick={() => {
                                    orderItemsAlphabetical();
                                    setExpanded(true);
                                }}
                            >
                                {translate("Show More")}
                            </Link>
                        ))}
                </StyledWrapper>
            </FancyScroll>
        </ManagedAccordionSection>
    );
};

export default connect(mapStateToProps)(ProductListFiltersAccordionSection);
